import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

interface ReportClientErrorArgs {
  feature: string
  error: unknown
  context?: Record<string, unknown>
}

const MAX_RESPONSE_RAW = 2000

/**
 * Send a structured frontend error report to the goapi (#833).
 *
 * Best-effort: failures are swallowed (we never want error reporting to
 * itself surface an error to the user). Also writes a `console.error` so
 * the same context is available in browser devtools.
 */
export async function reportClientError({ feature, error, context }: ReportClientErrorArgs): Promise<void> {
  const err = error as { response?: { status?: number; data?: unknown }; message?: string; config?: { url?: string } }
  const status = err?.response?.status ?? 0
  const url = err?.config?.url ?? ''
  let responseRaw = ''
  if (err?.response?.data !== undefined) {
    try {
      responseRaw = typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)
    } catch {
      responseRaw = String(err.response.data)
    }
    if (responseRaw.length > MAX_RESPONSE_RAW) {
      responseRaw = responseRaw.slice(0, MAX_RESPONSE_RAW) + '...(truncated)'
    }
  }

  const payload = {
    feature,
    message: err?.message ?? String(error),
    status,
    url,
    response_raw: responseRaw,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    context: context ?? {},
  }

  // Always log to console first so devs can see it even if the POST fails.
  // eslint-disable-next-line no-console
  console.error(`[${feature}] client error`, payload)

  try {
    await axios.post(`${API_BASE_URL}/client-errors/`, payload, {
      // Don't let interceptors retry/transform this — it's best-effort.
      timeout: 5000,
    })
  } catch {
    // Swallow — reporting failures must not cascade.
  }
}
